/**
 * Period totals for the calendar, over a work table that has ONE ROW PER DAY.
 *
 * A multi-day job is expanded into one view object per working day, each
 * carrying the WHOLE-JOB `estimate` (duration) and the same requestId/serviceId.
 * Summing naively therefore multi-counts by the number of days:
 *
 *   3-day job  →  3 views × 3 days   = 9 booked days   (should be 3)
 *   3-day job  →  3 views × $1,249   = $3,747 revenue  (should be $1,249)
 *
 * Both were latent before — they only bit jobs whose `workDates` already had
 * several entries — and became general when the server started expanding every
 * multi-day job into per-day rows.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/calendarTotals.test.mjs`. Do not add imports.
 */

/**
 * One day's share of a job's duration.
 *
 * The server collapses per-day rows back to a single appointment whose duration
 * is SUM(daily_work), so the per-day breakdown is no longer on the wire. Dividing
 * evenly reproduces the correct TOTAL, which is what capacity needs, even when a
 * 1.5-day job was really stored as 1.0 + 0.5.
 */
export function dayShare(duration, totalDays) {
  const d = Number(duration);
  if (!Number.isFinite(d) || d <= 0) return 0;
  const n = Number(totalDays);
  if (!Number.isFinite(n) || n <= 1) return d;
  return d / n;
}

/**
 * Booked days across the visible views — the numerator of capacity utilisation.
 *
 * Counts each day's share, so a job straddling the week boundary contributes
 * only its visible days. That is more correct than the previous whole-job count,
 * not merely a de-duplication.
 */
export function sumCapacityDays(views) {
  if (!Array.isArray(views)) return 0;
  return views.reduce((total, v) => {
    if (!v) return total;
    const share = Number(v.dayShare);
    if (Number.isFinite(share) && share > 0) return total + share;
    return total + dayShare(v.estimate, v.totalDays);
  }, 0);
}

/**
 * Net revenue across the visible views, counting each service line ONCE.
 *
 * Deliberately keyed on requestId+serviceId rather than on "is this day 1":
 * a job continuing from the previous week has no day-1 view in range, and its
 * revenue would vanish from the period entirely.
 *
 * `lookup(view)` returns the source appointment (the caller owns that join).
 */
export function sumNetRevenue(views, lookup) {
  if (!Array.isArray(views) || typeof lookup !== 'function') return 0;
  const seen = new Set();
  let total = 0;
  for (const v of views) {
    if (!v) continue;
    const key = `${v.requestId}::${v.serviceId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const appt = lookup(v) || {};
    const amount = parseFloat(appt.serviceAmount) || 0;
    const discount = parseFloat(appt.serviceDiscount) || 0;
    total += amount - discount;
  }
  return total;
}
